import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Trophy, Medal, Star, TrendingUp } from "lucide-react";
import axios from "axios";
import { staggerContainer, staggerItem } from "@/config/animations";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getRankIcon(rank) {
  if (rank === 1) return <Trophy className="w-5 h-5 text-yellow-400" />;
  if (rank === 2) return <Medal className="w-5 h-5 text-gray-300" />;
  if (rank === 3) return <Medal className="w-5 h-5 text-amber-600" />;
  return <span className="w-5 h-5 text-center text-sm text-[var(--qn-foreground-subtle)] font-mono">{rank}</span>;
}

export default function Leaderboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get(`${API}/leaderboard`).then(r => setData(r.data)).catch(() => {});
  }, []);

  if (!data || !data.leaderboard?.length) return null;

  return (
    <Card className="card-themed" data-testid="leaderboard">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Trophy className="w-4 h-4 text-yellow-400" />
          Savings Leaderboard
          <Badge variant="outline" className="ml-auto text-xs">This Month</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {data.message && (
          <div className="mb-4 p-3 rounded-lg bg-[var(--qn-primary-muted)] border border-[var(--qn-primary)]/20">
            <p className="text-sm flex items-center gap-2">
              <Star className="w-4 h-4 text-[var(--qn-primary)]" />
              <span className="text-[var(--qn-foreground)]">{data.message}</span>
            </p>
          </div>
        )}
        <motion.div variants={staggerContainer} initial="initial" animate="animate" className="space-y-2">
          {data.leaderboard.map((entry, i) => (
            <motion.div
              key={i}
              variants={staggerItem}
              className={`flex items-center gap-3 p-3 rounded-lg transition-all ${
                entry.is_you
                  ? "bg-[var(--qn-primary-muted)] border border-[var(--qn-primary)]/30"
                  : "bg-[var(--qn-surface)] hover:bg-[var(--qn-surface-hover)]"
              }`}
              data-testid={`leaderboard-entry-${i}`}
            >
              <div className="w-8 flex items-center justify-center">{getRankIcon(entry.rank)}</div>
              <div className="flex-1 min-w-0">
                <span className={`text-sm font-medium ${entry.is_you ? "text-[var(--qn-primary)]" : ""}`}>
                  {entry.name} {entry.is_you && <Badge className="ml-1 text-xs bg-[var(--qn-primary)] text-white">You</Badge>}
                </span>
              </div>
              <div className="text-sm font-mono font-bold text-[var(--qn-success)]">
                ${entry.savings.toFixed(2)}
              </div>
            </motion.div>
          ))}
        </motion.div>
      </CardContent>
    </Card>
  );
}
